/* Public native catalog only. No inquiry submission or persistent browser storage. */
(() => {
  'use strict';
  const kinds = ['shape', 'finish', 'size'];
  const dimensions = ['length', 'width', 'height', 'diameter', 'opening_diameter'];
  const units = new Set(['mm', 'cm', 'm', 'in', 'ft', 'yd']);
  const text = (value) => typeof value === 'string' && value.trim().length > 0;
  const publicPart = ({ name, code }) => ({ name, code });
  function catalogFrom(payload) {
    if (!payload || payload.version !== 1 || payload.complete !== true || payload.limit !== 250 ||
        !Number.isInteger(payload.total) || payload.total < 0 || payload.total > 250 ||
        !Array.isArray(payload.configurations) || payload.configurations.length > 250) {
      throw new Error('The complete product library is unavailable. Please contact the team.');
    }
    const objects = Object.fromEntries(kinds.map((kind) => [kind, new Map()]));
    const codes = Object.fromEntries(kinds.map((kind) => [kind, new Map()]));
    const ids = new Set(), configCodes = new Set(), triples = new Set();
    for (const config of payload.configurations) {
      if (!config || !text(config.id) || !text(config.name) || !text(config.code) || ids.has(config.id) || configCodes.has(config.code)) {
        throw new Error('The product library needs review before selection is available.');
      }
      ids.add(config.id); configCodes.add(config.code);
      for (const kind of kinds) {
        const item = config[kind];
        if (!item || !text(item.id) || !text(item.name) || !text(item.code)) throw new Error('A product reference is unavailable.');
        if (codes[kind].has(item.code) && codes[kind].get(item.code) !== item.id) throw new Error('A product code is duplicated.');
        if (objects[kind].has(item.id) && JSON.stringify(objects[kind].get(item.id)) !== JSON.stringify(item)) throw new Error('A product reference is inconsistent.');
        codes[kind].set(item.code, item.id); objects[kind].set(item.id, item);
        if (kind === 'size') {
          for (const key of dimensions) {
            const measure = item.dimensions?.[key];
            if (measure != null && (typeof measure.value !== 'number' || !Number.isFinite(measure.value) || measure.value <= 0 || !units.has(measure.unit))) {
              throw new Error('A product dimension needs review.');
            }
          }
        }
      }
      const triple = JSON.stringify(kinds.map((kind) => config[kind].id));
      if (triples.has(triple)) throw new Error('A product combination is duplicated. Please contact the team.');
      triples.add(triple);
    }
    return { configurations: payload.configurations, objects };
  }
  function optionsFor(catalog, selection, kind) {
    const index = kinds.indexOf(kind);
    if (index > 0 && !selection.shape) return [];
    if (index > 1 && !selection.finish) return [];
    const options = new Map();
    catalog.configurations.forEach((config) => {
      if (index > 0 && config.shape.id !== selection.shape) return;
      if (index > 1 && config.finish.id !== selection.finish) return;
      options.set(config[kind].id, config[kind]);
    });
    return [...options.values()].sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0) || a.name.localeCompare(b.name) || a.code.localeCompare(b.code));
  }
  function choose(catalog, selection, kind, id) {
    if (!kinds.includes(kind) || !optionsFor(catalog, selection, kind).some((item) => item.id === id)) return false;
    selection[kind] = id;
    // Reset dependents even when another parent shares the same option IDs.
    if (kind === 'shape') { selection.finish = null; selection.size = null; }
    if (kind === 'finish') selection.size = null;
    return true;
  }
  function selectedConfig(catalog, selection) {
    return catalog.configurations.find((config) => kinds.every((kind) => config[kind].id === selection[kind])) || null;
  }
  function summaryFor(config) {
    if (!config) return '';
    const lines = ['ANCOOLY configuration', `Configuration: ${config.name} (${config.code})`];
    kinds.forEach((kind) => lines.push(`${kind[0].toUpperCase() + kind.slice(1)}: ${config[kind].name} (${config[kind].code})`));
    dimensions.forEach((key) => {
      const measure = config.size.dimensions?.[key];
      if (measure) lines.push(`${key.replaceAll('_', ' ')}: ${measure.value} ${measure.unit}`);
    });
    lines.push('Specifications and availability require confirmation.');
    return lines.join('\n');
  }
  function safeImage(value) {
    if (typeof value !== 'string') return null;
    try {
      const url = new URL(value.startsWith('//') ? `https:${value}` : value);
      return url.protocol === 'https:' && !url.username && !url.password ? url.href : null;
    } catch (_) { return null; }
  }
  function initialize(root) {
    if (root.dataset.previewReady) return;
    root.dataset.previewReady = 'true';
    const status = root.querySelector('[data-acp-status]');
    let catalog;
    try { catalog = catalogFrom(JSON.parse(root.querySelector('[data-acp-catalog]').textContent)); }
    catch (_) {
      status.textContent = 'The complete product library is unavailable or needs review. Selection and inquiry saving are unavailable. Please contact the team.';
      root.dataset.catalogState = 'blocked';
      return;
    }
    const selection = { shape: null, finish: null, size: null };
    const copy = root.querySelector('[data-acp-copy]');
    const copyText = root.querySelector('[data-acp-copy-text]');
    const copyStatus = root.querySelector('[data-acp-copy-status]');
    const steps = Array.from(root.querySelectorAll('.acp__steps a'));
    const newElement = (tag, value, className) => {
      const element = document.createElement(tag);
      if (value != null) element.textContent = value;
      if (className) element.className = className;
      return element;
    };
    function render() {
      root.dataset.catalogState = catalog.configurations.length ? 'ready' : 'empty';
      status.textContent = catalog.configurations.length
        ? (root.dataset.inquiryLinked === 'true'
          ? 'Choose a published combination, copy your summary, then paste it into the separate inquiry form. Nothing is saved by this selector.'
          : 'Choose a published shape, finish and size combination. You can copy your summary. Inquiry saving is not connected.')
        : 'No published compatible configurations are available yet. The merchant must add and activate product entries and their combinations. Nothing is saved or sent by this selector.';
      kinds.forEach((kind) => {
        const container = root.querySelector(`[data-acp-choices="${kind}"]`);
        const options = optionsFor(catalog, selection, kind);
        root.querySelector(`[data-acp-count="${kind}"]`).textContent = `${options.length} ${kind}${options.length === 1 ? '' : (kind === 'finish' ? 'es' : 's')}`;
        container.replaceChildren();
        if (!options.length) container.append(newElement('p', kind === 'shape' ? 'No published shapes with compatible configurations yet.' : `Choose a ${kind === 'finish' ? 'shape' : 'finish'} first.`));
        options.forEach((item) => {
          const button = newElement('button', null, 'acp__choice');
          button.type = 'button'; button.dataset.optionId = item.id;
          button.setAttribute('aria-pressed', String(selection[kind] === item.id));
          const imageUrl = safeImage(item.image);
          if (imageUrl) {
            const image = newElement('img'); image.src = imageUrl; image.alt = ''; image.loading = 'lazy'; image.width = 240; image.height = 180; button.append(image);
          }
          button.append(newElement('strong', item.name), newElement('span', item.code, 'acp__choice-code'));
          if (typeof item.description === 'string' && item.description) button.append(newElement('span', item.description, 'acp__choice-description'));
          if (kind === 'size') {
            dimensions.forEach((key) => {
              const measure = item.dimensions?.[key];
              if (measure) button.append(newElement('span', `${key.replaceAll('_', ' ')}: ${measure.value} ${measure.unit}`, 'acp__dimension'));
            });
          }
          button.addEventListener('click', () => {
            if (!choose(catalog, selection, kind, item.id)) return;
            render();
            // Rendering replaces the clicked button; restore keyboard focus.
            const replacement = [...container.querySelectorAll('button')].find((entry) => entry.dataset.optionId === item.id);
            if (replacement) replacement.focus({ preventScroll: true });
          });
          container.append(button);
        });
        const item = catalog.objects[kind].get(selection[kind]);
        root.querySelector(`[data-acp-summary="${kind}"]`).textContent = item ? `${item.name} (${item.code})` : 'Not selected';
      });
      const config = selectedConfig(catalog, selection);
      copyText.value = summaryFor(config); copy.disabled = !config;
      copyStatus.textContent = 'Copying does not save or send an inquiry.';
      const art = root.querySelector('[data-acp-art]');
      art.replaceChildren();
      const selectedShape = catalog.objects.shape.get(selection.shape);
      const imageUrl = safeImage(selectedShape?.image);
      if (imageUrl) { const image = newElement('img'); image.src = imageUrl; image.alt = ''; image.width = 300; image.height = 180; art.append(image); }
      else art.append(newElement('span', selectedShape ? 'No shape image provided' : 'YOUR SELECTION WILL APPEAR HERE'));
      // Future integration boundary only; NOT persistence or submit authorization.
      const detail = config ? { configuration: publicPart(config), shape: publicPart(config.shape), finish: publicPart(config.finish), size: publicPart(config.size), summary: summaryFor(config) } : null;
      root.dispatchEvent(new CustomEvent('ancooly:selection-change', { bubbles: true, detail }));
    }
    copy.addEventListener('click', async () => {
      const config = selectedConfig(catalog, selection);
      if (!config) return;
      const snapshot = summaryFor(config);
      copyText.value = snapshot;
      try {
        if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(snapshot);
        if (copyText.value === snapshot) copyStatus.textContent = 'Configuration copied. It has not been saved or sent.';
      } catch (_) {
        if (copyText.value !== snapshot) return;
        copyText.focus(); copyText.select();
        copyStatus.textContent = 'Select and copy the summary above. It has not been saved or sent.';
      }
    });
    root.querySelectorAll('a[href^="#acp-"]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const target = document.getElementById(link.getAttribute('href').slice(1));
        if (!target || !root.contains(target)) return;
        event.preventDefault();
        steps.forEach((step) => {
          if (step.getAttribute('href') === link.getAttribute('href')) step.setAttribute('aria-current', 'step');
          else step.removeAttribute('aria-current');
        });
        target.focus({ preventScroll: true });
        target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      });
    });
    render();
  }
  function initializeAll() { document.querySelectorAll('[data-ancooly-customizer]').forEach(initialize); }
  if (typeof document !== 'undefined') {
    initializeAll();
    document.addEventListener('shopify:section:load', initializeAll);
  }
  // Pure functions can be tested with Node; nothing is exported on the storefront.
  if (typeof module !== 'undefined' && module.exports) module.exports = { catalogFrom, optionsFor, choose, selectedConfig, summaryFor, safeImage };
})();
